import { Inject, Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { UserNavigationItem } from '@/app/platform/platform-core/user/models/user-navigation-item';
import { Notifier, NOTIFIER } from '@shared/notifier/types';
import { PlatformModeService } from '@/app/platform/platform-core/platform-mode/platform-mode.service';
import { EGI_APP_ID_ROUTE_PARAM } from '../../../../../../projects/page-framework/src/lib/constants';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationGuard  {
  constructor(
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    private readonly router: Router,
    @Inject(NOTIFIER)
    private readonly notifier: Notifier
  ) {}

  private readonly platformMode = inject(PlatformModeService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Alleen volledige toegang (issue #51): de apps verdwijnen pas bij de volgende refresh van de
    // gebruikersinfo; tot dan houdt de guard ze al dicht.
    if (this.platformMode.appsClosedForMe()) {
      this.router.navigate(['']);
      return false;
    }

    const app = this.extractApp(route);

    // Een app die niet (meer) in de lijst staat (bladwijzer, of weggelaten door de platformmodus)
    if (!app || !this.hasAccessToApp(app)) {
      this.router.navigate(['']);
      return false;
    }
    const navigationItem = this.extractNavigationItem(app, route);

    // Een pagina die niet in de navigatie van de gebruiker staat (nog niet toegekend, of weggehaald)
    // is geen toegang. Zonder de ?. gooide de guard hier een TypeError en gebeurde er niets
    // (Sentry 2026-10-30, nieuwe pagina vóór de toekenning in UPAM). Zie authorization.guard.spec.ts.
    if (!navigationItem?.isBaseAccessAllowed) {
      this.notifier.warning('U hebt geen toegang tot deze pagina!');
      return false;
    }

    // De titel van de browsertab hoort hier niet: PageTitleStrategy zet hem na elke navigatie
    // (ook op login- en publieke pagina's, waar deze guard niet draait).
    return true;
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(childRoute, state);
  }

  private hasAccessToApp(app: UserEgiApp): boolean {
    return app.rootNavigationItem.isBaseAccessAllowed;
  }

  private extractApp(route: ActivatedRouteSnapshot): UserEgiApp {
    const userEgiApps = this.userQueries.getAvailableApps();

    const appId = route.paramMap.get(EGI_APP_ID_ROUTE_PARAM);

    return userEgiApps.find(a => a.id === appId);
  }

  private extractNavigationItem(
    app: UserEgiApp,
    route: ActivatedRouteSnapshot
  ): UserNavigationItem | undefined {
    const navId = route.queryParamMap.get('navId');
    return app.rootNavigationItem.clone().asArray.find(i => i.id === navId);
  }
}
